/**
 * FloatingPointsAnimation - Animated notification that floats towards the StatsBar
 * when points (XP, Gems, Hearts, Streaks) are earned.
 */
import React, { useEffect, useState } from 'react';
import { Star, Diamond, Heart, Flame, Sparkles } from 'lucide-react';
import { usePreferences } from '@/context/PreferencesContext';

const ANIMATION_DURATION = 1000; // 1 second

export default function FloatingPointsAnimation({
  type, // 'xp', 'gems', 'hearts', 'streak'
  amount, // positive number for gains, negative for losses
  startPosition, // { x, y } - starting position on screen
  targetPosition, // { x, y } - StatsBar position (usually top-right)
  onComplete
}) {
  const [position, setPosition] = useState(startPosition || { x: window.innerWidth / 2, y: window.innerHeight / 2 });
  const [opacity, setOpacity] = useState(1);
  const [scale, setScale] = useState(1);
  const { playCelebrationSound } = usePreferences();

  useEffect(() => {
    if (!targetPosition) return;

    // Play celebration sound for XP or gems (positive amounts only)
    if ((type === 'xp' || type === 'gems') && amount > 0 && playCelebrationSound) {
      playCelebrationSound();
    }

    const startTime = Date.now();
    
    const animate = () => {
      const elapsed = Date.now() - startTime;
      const progress = Math.min(elapsed / ANIMATION_DURATION, 1);
      
      // Ease-out function for smooth deceleration
      const easeOut = 1 - Math.pow(1 - progress, 3);
      
      // Interpolate position
      const currentX = startPosition.x + (targetPosition.x - startPosition.x) * easeOut;
      const currentY = startPosition.y + (targetPosition.y - startPosition.y) * easeOut;
      
      setPosition({ x: currentX, y: currentY });
      
      // Fade out as it approaches target
      if (progress > 0.7) {
        setOpacity(1 - (progress - 0.7) / 0.3);
      }
      
      // Scale animation (bounce effect at start)
      if (progress < 0.2) {
        setScale(1 + Math.sin(progress * Math.PI * 5) * 0.3);
      } else {
        setScale(1 - progress * 0.3);
      }
      
      if (progress < 1) {
        requestAnimationFrame(animate);
      } else {
        onComplete?.();
      }
    };
    
    requestAnimationFrame(animate);
  }, [startPosition, targetPosition, onComplete, type, amount, playCelebrationSound]);

  const getIcon = () => {
    switch (type) {
      case 'xp':
        return <Star className="w-8 h-8 text-yellow-400" />;
      case 'gems':
        return <Diamond className="w-6 h-6 text-purple-400" />;
      case 'hearts':
        return <Heart className="w-6 h-6 text-pink-400" />;
      case 'streak':
        return <Flame className="w-6 h-6 text-orange-400" />;
      default:
        return <Star className="w-8 h-8 text-yellow-400" />;
    }
  };

  const getColorClass = () => {
    switch (type) {
      case 'xp':
        return 'text-yellow-400';
      case 'gems':
        return 'text-purple-400';
      case 'hearts':
        return amount > 0 ? 'text-pink-400' : 'text-red-400';
      case 'streak':
        return 'text-orange-400';
      default:
        return 'text-yellow-400';
    }
  };

  const displayAmount = amount > 0 ? `+${amount}` : `${amount}`;

  return (
    <div
      className="fixed pointer-events-none z-50 transition-all duration-300"
      style={{
        left: `${position.x}px`,
        top: `${position.y}px`,
        transform: `translate(-50%, -50%) scale(${scale})`,
        opacity: opacity,
      }}
    >
      <div className={`flex items-center gap-3 bg-black/90 backdrop-blur-md rounded-full px-5 py-3 shadow-2xl border-2 border-white/30 relative`}>
        {/* Glow effect */}
        <div className={`absolute inset-0 rounded-full bg-gradient-to-r ${type === 'xp' ? 'from-yellow-400/50 to-amber-500/50' : type === 'hearts' ? 'from-pink-400/50 to-rose-500/50' : 'from-purple-400/50 to-pink-500/50'} blur-xl animate-pulse`} />
        {/* Content */}
        <div className="relative z-10 flex items-center gap-3">
          <div className={`${type === 'xp' ? 'w-8 h-8' : 'w-6 h-6'} flex items-center justify-center`}>
            {getIcon()}
          </div>
          <span className={`text-2xl font-extrabold ${getColorClass()} drop-shadow-lg`}>
            {displayAmount}
          </span>
          {type === 'xp' && (
            <>
              <Sparkles className="w-5 h-5 text-yellow-300 animate-pulse" />
              <Sparkles className="w-4 h-4 text-amber-300 animate-ping absolute -top-1 -right-1" />
            </>
          )}
        </div>
      </div>
    </div>
  );
}

/**
 * Hook to manage floating animations
 */
export function useFloatingPointsAnimation() {
  const [animations, setAnimations] = useState([]);

  const addAnimation = (type, amount, startPosition, targetPosition) => {
    const id = Date.now() + Math.random();
    const animation = {
      id,
      type,
      amount,
      startPosition,
      targetPosition,
    };
    
    setAnimations(prev => [...prev, animation]);
    
    // Auto-remove after animation completes
    setTimeout(() => {
      setAnimations(prev => prev.filter(a => a.id !== id));
    }, ANIMATION_DURATION + 100);
  };

  const renderAnimations = () => {
    return animations.map(anim => (
      <FloatingPointsAnimation
        key={anim.id}
        type={anim.type}
        amount={anim.amount}
        startPosition={anim.startPosition}
        targetPosition={anim.targetPosition}
        onComplete={() => {
          setAnimations(prev => prev.filter(a => a.id !== anim.id));
        }}
      />
    ));
  };

  return { addAnimation, renderAnimations };
}
